import { useEffect, useRef } from 'react';

// Global modal registry to track active modals
const activeModals = new Set<string>();
const modalCallbacks = new Map<string, () => void>();

// Modal stack for proper back navigation (last opened, first closed)
const modalStack: string[] = [];

// Modal metadata for enhanced navigation
const modalMetadata = new Map<string, {
  title?: string;
  priority?: number;
  isNested?: boolean;
  parentModal?: string;
  onBack?: () => boolean; // Return true if back handled
}>();

export const useGlobalModal = (
  modalId: string, 
  isOpen: boolean, 
  onClose: () => void,
  options?: {
    title?: string;
    priority?: number;
    isNested?: boolean;
    parentModal?: string;
    onBack?: () => boolean;
  }
) => {
  const wasOpenRef = useRef(false);
  
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal is opening
      console.log(`🌟 GLOBAL MODAL: Opening modal ${modalId}`);
      console.log(`🌟 GLOBAL MODAL: Currently active modals:`, Array.from(activeModals));
      
      // Store modal metadata
      if (options) {
        modalMetadata.set(modalId, options);
      }
      
      // Add to modal stack (last opened, first closed)
      modalStack.push(modalId);
      
      // Register this modal as active
      activeModals.add(modalId);
      modalCallbacks.set(modalId, onClose);
      wasOpenRef.current = true;
      
    } else if (!isOpen && wasOpenRef.current) {
      // Modal is closing
      console.log(`🌟 GLOBAL MODAL: Closing modal ${modalId}`);
      
      // Remove from stack and metadata
      const stackIndex = modalStack.indexOf(modalId);
      if (stackIndex > -1) {
        modalStack.splice(stackIndex, 1);
      }
      modalMetadata.delete(modalId);
      
      activeModals.delete(modalId);
      modalCallbacks.delete(modalId);
      wasOpenRef.current = false;
    }
  }, [isOpen, modalId, onClose, options]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log(`🌟 GLOBAL MODAL: Cleanup modal ${modalId}`);
      activeModals.delete(modalId);
      modalCallbacks.delete(modalId);
      
      const stackIndex = modalStack.indexOf(modalId);
      if (stackIndex > -1) {
        modalStack.splice(stackIndex, 1);
      }
      modalMetadata.delete(modalId);
    };
  }, [modalId]);
  
  return {
    shouldRender: isOpen,
    activeModalCount: activeModals.size
  };
};

// Enhanced modal management functions
export const getModalStack = () => [...modalStack];
export const getActiveModals = () => [...activeModals];
export const getTopModal = () => modalStack[modalStack.length - 1] || null;
export const getModalMetadata = (modalId: string) => modalMetadata.get(modalId);

// Modal back navigation function
export const handleModalBack = (): boolean => {
  const topModalId = getTopModal();
  
  if (!topModalId) {
    console.log('🌟 GLOBAL MODAL: No active modals for back navigation');
    return false;
  }
  
  const metadata = getModalMetadata(topModalId);
  
  // If modal has custom back handler, use it
  if (metadata?.onBack) {
    console.log(`🌟 GLOBAL MODAL: Custom back handler for ${topModalId}`);
    const handled = metadata.onBack();
    if (handled) {
      return true;
    }
  }
  
  // Default back behavior: close the top modal
  console.log(`🌟 GLOBAL MODAL: Closing top modal ${topModalId}`);
  const closeCallback = modalCallbacks.get(topModalId);
  if (closeCallback) {
    closeCallback();
    return true;
  }
  
  return false;
};

// Check if back should close modal or navigate
export const shouldCloseModalOnBack = (): boolean => {
  return modalStack.length > 0;
};

// Get modal hierarchy information
export const getModalHierarchy = () => {
  return modalStack.map(modalId => ({
    id: modalId,
    metadata: getModalMetadata(modalId)
  }));
};

// Enhanced modal detection - detect any open modal in DOM
export const detectOpenModals = (): Array<{
  element: HTMLElement;
  modalId?: string;
  hasCloseButton: boolean;
  closeButtons: HTMLElement[];
  type: 'global' | 'dom';
  priority: number;
}> => {
  const openModals: Array<{
    element: HTMLElement;
    modalId?: string;
    hasCloseButton: boolean;
    closeButtons: HTMLElement[];
    type: 'global' | 'dom';
    priority: number;
  }> = [];

  // Check global modal stack first (highest priority)
  if (modalStack.length > 0) {
    modalStack.forEach(modalId => {
      const element = document.querySelector(`[data-modal-id="${modalId}"]`) as HTMLElement;
      if (element && element.offsetParent !== null) { // Element is visible
        const closeButtons = element.querySelectorAll('button[aria-label*="close"], button[aria-label*="Close"], .modal-close, .close-button, [data-close-modal]');
        openModals.push({
          element,
          modalId,
          hasCloseButton: closeButtons.length > 0,
          closeButtons: Array.from(closeButtons) as HTMLElement[],
          type: 'global',
          priority: 100 + modalStack.indexOf(modalId) // Higher priority for global modals
        });
      }
    });
  }

  // Detect any modal-like elements in DOM (fallback)
  const modalSelectors = [
    // Fixed overlay dialogs
    '[role="dialog"]',
    '[aria-modal="true"]',
    // Common modal class patterns
    '.modal',
    '.modal-overlay',
    '.modal-dialog',
    '.modal-content',
    // React component patterns (fixed positioning with backdrop)
    'div[class*="fixed"][class*="inset-0"] > div[class*="rounded"], div[class*="fixed"][class*="inset-0"] > div[class*="shadow"]',
    // Specific component patterns
    'div[class*="z-"] > div[class*="bg-"][class*="rounded"]:not([class*="absolute"])',
    // AlertDialog patterns
    '[data-state="open"]',
    // Custom modal patterns
    '[data-modal="true"]'
  ];

  modalSelectors.forEach(selector => {
    try {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        const htmlElement = element as HTMLElement;
        
        // Skip if already detected or if element is not visible
        if (openModals.some(modal => modal.element === htmlElement) || 
            htmlElement.offsetParent === null) {
          return;
        }

        // Check if this looks like a modal (has backdrop overlay)
        let hasBackdrop = false;
        let parent = htmlElement.parentElement;
        
        while (parent && parent !== document.body) {
          if (parent.classList.contains('fixed') || 
              parent.classList.contains('absolute') ||
              parent.getAttribute('role') === 'dialog' ||
              parent.getAttribute('aria-modal') === 'true' ||
              parent.classList.contains('modal-overlay')) {
            hasBackdrop = true;
            break;
          }
          parent = parent.parentElement;
        }

        // Find close buttons within or on this element
        const closeSelectors = [
          'button[aria-label*="close"]',
          'button[aria-label*="Close"]',
          'button[aria-label*="Cancel"]',
          'button[aria-label*="cancel"]',
          'button[title*="close"]',
          'button[title*="Close"]',
          '.modal-close',
          '.close-button',
          '[data-close-modal]',
          'button[class*="close"]',
          'svg[class*="close"]',
          // Common close icon patterns
          'button svg.lucide-x',
          'button svg[data-lucide="x"]'
        ];

        const closeButtons: HTMLElement[] = [];
        closeSelectors.forEach(closeSelector => {
          try {
            const buttons = htmlElement.querySelectorAll(closeSelector);
            buttons.forEach(btn => {
              const button = btn as HTMLElement;
              if (button.offsetParent !== null) { // Visible button
                closeButtons.push(button);
              }
            });
          } catch {
            // Skip invalid selectors
          }
        });

        // Also check parent elements for close buttons
        if (parent && hasBackdrop) {
          closeSelectors.forEach(closeSelector => {
            try {
              const buttons = parent.querySelectorAll(closeSelector);
              buttons.forEach(btn => {
                const button = btn as HTMLElement;
                if (button.offsetParent !== null) { // Visible button
                  closeButtons.push(button);
                }
              });
            } catch {
              // Skip invalid selectors
            }
          });
        }

        // Consider it a modal if it has backdrop or close buttons
        const hasCloseButton = closeButtons.length > 0;
        const isModal = hasBackdrop || hasCloseButton || 
                       htmlElement.getAttribute('role') === 'dialog' ||
                       htmlElement.getAttribute('aria-modal') === 'true';

        if (isModal) {
          openModals.push({
            element: htmlElement,
            modalId: htmlElement.id || htmlElement.className || 'dom-modal',
            hasCloseButton,
            closeButtons,
            type: 'dom',
            priority: 50 // Lower priority than global modals
          });
        }
      });
    } catch (e) {
      // Skip invalid selectors
      console.warn('Invalid modal selector:', selector, e);
    }
  });

  // Sort by priority (highest first)
  return openModals.sort((a, b) => b.priority - a.priority);
};

// Enhanced check for any open modal
export const hasAnyOpenModal = (): boolean => {
  // Check global modal stack first
  if (shouldCloseModalOnBack()) {
    return true;
  }

  // Check DOM for any open modal
  const openModals = detectOpenModals();
  return openModals.length > 0;
};

// Find the topmost modal with close button
export const findTopModalWithCloseButton = (): {
  element: HTMLElement;
  modalId?: string;
  closeButtons: HTMLElement[];
  type: 'global' | 'dom';
} | null => {
  const openModals = detectOpenModals();
  
  // Find modal with close button, highest priority first
  const modalWithClose = openModals.find(modal => modal.hasCloseButton);
  
  if (modalWithClose) {
    return {
      element: modalWithClose.element,
      modalId: modalWithClose.modalId,
      closeButtons: modalWithClose.closeButtons,
      type: modalWithClose.type
    };
  }
  
  // If no modal with close button, return topmost modal
  if (openModals.length > 0) {
    const topModal = openModals[0];
    return {
      element: topModal.element,
      modalId: topModal.modalId,
      closeButtons: topModal.closeButtons,
      type: topModal.type
    };
  }
  
  return null;
};

// Try to close a modal using its close buttons
export const tryCloseModalWithButton = (modal: {
  element: HTMLElement;
  closeButtons: HTMLElement[];
}): boolean => {
  // Try each close button
  for (const closeButton of modal.closeButtons) {
    try {
      // Check if button is clickable
      if (closeButton.disabled || closeButton.offsetParent === null) {
        continue;
      }

      console.log('🔙 Attempting to close modal using close button:', closeButton);
      
      // Click the close button
      closeButton.click();
      
      // Give a moment for the modal to close
      setTimeout(() => {
        // Verify modal closed
        if (modal.element.offsetParent === null) {
          console.log('✅ Modal closed successfully via close button');
        }
      }, 100);
      
      return true;
    } catch (error) {
      console.warn('⚠️ Failed to click close button:', error);
      continue;
    }
  }
  
  return false;
};